import { plural } from '../Documents/format';
import {
  InvalidReason, ListQuery, NcelsCandidate, NcelsSource, NcelsStatus, NktStatus, NomenclatureMeta, ProductSources, SortDir, SortKey
} from './nomenclatureApi';

export type FilterKey = 'type' | 'category' | 'priceGroup' | 'folder' | 'rxotc' | 'photo' | 'marked' | 'nkt' | 'ncels';
export type Filters = Record<FilterKey, string>;
export interface Sort { key: SortKey; dir: SortDir }
export interface Option { value: string; label: string; count?: number }

/** Значение фильтра «не заполнено в 1С» для словарных полей */
export const NONE = '__none';

export const EMPTY_FILTERS: Filters = {
  type: '', category: '', priceGroup: '', folder: '', rxotc: '', photo: '', marked: '', nkt: '', ncels: ''
};
export const DEFAULT_SORT: Sort = { key: 'name', dir: 'asc' };

export const countFilters = (f: Filters): number => (Object.keys(f) as FilterKey[]).filter(k => f[k]).length;

export const toQuery = (term: string, f: Filters, sort: Sort): ListQuery => ({ term, ...f, sort: sort.key, dir: sort.dir });

export const NKT_LABELS: Record<NktStatus, string> = {
  found: 'Найден в НКТ', ofd: 'Только ОФД', not_found: 'Не найден', pending: 'Ожидает сверки',
  error: 'Ошибка сверки', invalid: 'Штрихкод не GTIN', none: 'Нет штрихкода'
};

export const NCELS_LABELS: Record<NcelsStatus, string> = {
  found: 'Найдено РУ', multiple: 'Несколько РУ', not_found: 'РУ не найдено', pending: 'Ожидает сверки',
  error: 'Ошибка сверки', skipped: 'Не лекарство', none: 'Нет GTIN'
};

export const NKT_HINTS: Record<NktStatus, string> = {
  found: 'Карточка товара есть в Национальном каталоге товаров',
  ofd: 'Карточки в НКТ нет, но штрихкод известен ОФД (nct.gov.kz): название и признак маркировки - оттуда',
  not_found: 'Штрихкод не знают ни НКТ, ни ОФД',
  pending: 'Позиция в очереди фоновой сверки',
  error: 'Источник не ответил - сверка повторится автоматически',
  invalid: 'Не удалось найти',
  none: 'В 1С у товара нет штрихкода'
};

export const NCELS_HINTS: Record<NcelsStatus, string> = {
  found: 'Регистрационное удостоверение найдено в госреестре НЦЭЛС',
  multiple: 'Под название подходит несколько удостоверений - показаны общие для них сведения',
  not_found: 'В госреестре НЦЭЛС удостоверение по названию не найдено',
  pending: 'Ждёт сверки: РУ ищется после ответа НКТ',
  error: 'Госреестр не ответил - поиск повторится автоматически',
  // skipped: 'В 1С товар не относится к медикаментам - РУ не ищется',
  skipped: 'Не лекарство',
  none: 'Без корректного штрихкода РУ не ищется'
};

const GREEN = { color: '#047857', bg: 'rgba(16,185,129,0.1)' };
const BLUE = { color: '#1d4ed8', bg: 'rgba(37,99,235,0.08)' };
const AMBER = { color: '#b45309', bg: 'rgba(245,158,11,0.12)' };
const RED = { color: '#b91c1c', bg: 'rgba(239,68,68,0.08)' };
const VIOLET = { color: '#6d28d9', bg: 'rgba(124,58,237,0.08)' };
const GRAY = { color: '#64748b', bg: '#f1f5f9' };
const MUTED = { color: '#94a3b8', bg: '#f8fafc' };

export const NKT_COLORS: Record<NktStatus, { color: string; bg: string }> = {
  found: GREEN, ofd: BLUE, not_found: GRAY, pending: VIOLET, error: RED, invalid: MUTED, none: MUTED
};

export const NCELS_COLORS: Record<NcelsStatus, { color: string; bg: string }> = {
  found: GREEN, multiple: AMBER, not_found: GRAY, pending: VIOLET, error: RED, skipped: MUTED, none: MUTED
};

export const INVALID_LABELS: Record<InvalidReason, string> = {
  format: 'не цифровой или неверной длины',
  check: 'неверная контрольная цифра',
  internal: 'внутренний код'
};

const statusOptions = <T extends string>(labels: Record<T, string>): Option[] =>
  (Object.keys(labels) as T[]).map(value => ({ value, label: labels[value] }));

/** Варианты фильтров: словари 1С - из meta (со счётчиками), остальные - перечислимые */
export function filterOptions(meta: NomenclatureMeta): Record<FilterKey, Option[]> {
  const dict = (key: 'type' | 'category' | 'priceGroup' | 'folder'): Option[] => [
    ...(meta.filters ? meta.filters[key] : []).map(o => ({ value: o.value, label: o.value, count: o.count })),
    { value: NONE, label: 'не указано' }
  ];
  return {
    type: dict('type'),
    category: dict('category'),
    priceGroup: dict('priceGroup'),
    folder: dict('folder'),
    rxotc: [
      ...(meta.filters ? meta.filters.rxotc : []).map(o => ({ value: o.value, label: o.value, count: o.count })),
      { value: 'none', label: 'не указано' }
    ],
    photo: [{ value: 'yes', label: 'Есть изображение' }, { value: 'no', label: 'Нет изображения' }],
    marked: [{ value: 'yes', label: 'Подлежит маркировке' }, { value: 'no', label: 'Не подлежит' }],
    nkt: statusOptions(NKT_LABELS),
    ncels: statusOptions(NCELS_LABELS)
  };
}

/** Подписи фильтров на панели */
export const FILTER_LABELS: Record<FilterKey, string> = {
  type: 'Тип', category: 'Категория', priceGroup: 'Ценовая группа', folder: 'Папка', rxotc: 'Rx / OTC',
  photo: 'Изображение', marked: 'Маркировка', nkt: 'НКТ', ncels: 'НЦЭЛС'
};

/** Время сверки (ISO, UTC) - по Алматы: «28.10.2026, 14:05» */
export function fmtStamp(iso?: string | null): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!isFinite(t)) return '-';
  return new Date(t).toLocaleString('ru-RU', {
    timeZone: 'Asia/Almaty', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

/** Номенклатура из 1С обновляется каждые 30 минут: старше 2 часов - выгрузка, похоже, не работает */
const STALE_HOURS = 2;

export interface Freshness { updated: string | null; ageLabel: string | null; stale: boolean }

/** Свежесть номенклатуры 1С: время по Алматы и давность «N мин назад» */
export function freshnessOf(iso: string | null | undefined, now = Date.now()): Freshness {
  const t = iso ? Date.parse(iso) : NaN;
  if (!isFinite(t)) return { updated: null, ageLabel: null, stale: true };
  const minutes = Math.max(0, Math.round((now - t) / 60000));
  let ageLabel: string;
  if (minutes < 1) ageLabel = 'только что';
  else if (minutes < 60) ageLabel = `${minutes} мин назад`;
  else if (minutes < 48 * 60) ageLabel = `${Math.floor(minutes / 60)} ч назад`;
  else ageLabel = `${Math.floor(minutes / 1440)} дн. назад`;
  return { updated: fmtStamp(iso), ageLabel, stale: minutes > STALE_HOURS * 60 };
}

/** Фоновая сверка ещё не прошла все позиции (или ждёт окончания обновления 1С) */
export function enrichmentBusy(meta: NomenclatureMeta | null): boolean {
  const e = meta && meta.enrichment;
  if (!e) return false;
  if (e.pausedForSync) return true;
  const s = e.stats;
  return Boolean(s && (s.nktChecked < s.valid || s.ncelsChecked < s.drugs));
}

/** Дата из госреестра («2020-05-12» или «2020-05-12T00:00:00») - «12.05.2020» без сдвига часового пояса */
export function fmtRegDate(value?: string | null): string | null {
  const m = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

/** Срок РУ истёк: дата окончания раньше сегодняшней */
export function regExpired(value?: string | null): boolean {
  const m = value ? /^(\d{4}-\d{2}-\d{2})/.exec(value) : null;
  return Boolean(m && m[1] < new Date().toISOString().slice(0, 10));
}

/** По какому признаку найдена карточка НКТ / ответ ОФД */
export function nktFoundBy(s: ProductSources): string | null {
  const gtin = s.gtin && s.gtin !== s.barcode ? ` (GTIN ${s.gtin})` : '';
  if (s.nkt.status === 'found') return `Найдено по штрихкоду ${s.barcode}${gtin}`;
  if (s.nkt.status === 'ofd') return `В НКТ карточки нет, ОФД знает штрихкод ${s.barcode}${gtin}`;
  if (s.nkt.status === 'not_found') return `Искали по штрихкоду ${s.barcode}${gtin}`;
  return null;
}

const NAME_FROM: Record<'nkt' | 'ofd' | '1c', string> = { nkt: 'из карточки НКТ', ofd: 'из ОФД', '1c': 'из 1С' };

/**
 * По какому признаку найдено РУ: название из НКТ (точнее всего - его даёт GTIN), ОФД или 1С,
 * при нескольких подходящих - уточнение числом в упаковке или дозировкой
 */
export function ncelsFoundBy(n: NcelsSource): string | null {
  const q = n.query;
  if (!q) return null;
  const parts = [`названию «${q.tradeName || q.name}» ${NAME_FROM[q.from] || ''}`.trim()];
  if (n.match === 'units' && q.units) parts.push(`числу в упаковке №${q.units}`);
  if (n.match === 'dose' && q.dose) parts.push(`дозировке ${q.dose}`);
  const by = parts.join(' и ');
  if (n.status === 'found') return `Найдено по ${by}`;
  if (n.status === 'multiple') return `Несколько удостоверений по ${by}`;
  if (n.status === 'not_found') return `Искали по ${by}`;
  return null;
}

/** Сведения, одинаковые у всех кандидатов «Несколько РУ» - их можно показать как достоверные */
export function candidatesCommon(list: NcelsCandidate[]): Array<{ label: string; value: string }> {
  const fields: Array<{ key: keyof NcelsCandidate; label: string }> = [
    { key: 'tradeName', label: 'Торговое название' },
    { key: 'form', label: 'Лекарственная форма' },
    { key: 'producer', label: 'Производитель' },
    { key: 'country', label: 'Страна' }
  ];
  if (!list.length) return [];
  return fields.flatMap(f => {
    const values = list.map(c => String(c[f.key] || '').trim());
    const first = values[0];
    return first && values.every(v => v.toLowerCase() === first.toLowerCase()) ? [{ label: f.label, value: first }] : [];
  });
}

/** Число из НКТ (packQty) может прийти строкой */
export function fmtPackQty(v: number | string | null | undefined): string | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isFinite(n) ? n.toLocaleString('ru-RU') : String(v);
}

/** Количество вещества: «250 мг» */
export function fmtAmount(amount: number | null, measure: string | null): string {
  if (amount === null || amount === undefined) return measure || '';
  return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 4 })}${measure ? ` ${measure}` : ''}`;
}

const TERM_UNITS: Array<[RegExp, [string, string, string]]> = [
  [/^(год|лет)/i, ['год', 'года', 'лет']],
  [/^мес/i, ['месяц', 'месяца', 'месяцев']],
  [/^(дн|ден)/i, ['день', 'дня', 'дней']]
];

/** Срок хранения из реестра («3 Год») по-русски: «3 года» */
export function fmtTerm(v: string | null): string | null {
  const m = v ? /^(\d+)\s+(.+)$/.exec(v.trim()) : null;
  if (!m) return v;
  const unit = TERM_UNITS.find(([re]) => re.test(m[2]));
  return unit ? `${m[1]} ${plural(Number(m[1]), ...unit[1])}` : v;
}

/** Значения без повторов: реестр и НКТ часто дублируют одно и то же в соседних полях */
export function distinctText(values: Array<string | number | null | undefined>): string[] {
  const seen = new Set<string>();
  return values
    .map(v => (v === null || v === undefined ? '' : String(v).trim()))
    .filter(v => {
      const key = v.toLowerCase();
      if (!v || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}
